/**
 * The first question: which subject area is this report about?
 *
 * A `<select>` was the wrong control for it. Choosing between models discards
 * the draft and re-stocks every picker below — it is the report's premise, not
 * a setting — and a native menu can show nothing but a noun on each row.
 * "Utilization" and "Timesheets" both plausibly hold "hours by customer", so
 * the noun alone makes the choice a guess you correct after building the wrong
 * report. What is missing is the sentence saying what each one answers.
 *
 * Laying all of them out as cards would show those sentences, and does not
 * survive the subject areas still to come: a dozen of them is a wall of prose
 * above the shelves, on every visit, to make one choice. So this is the SAME
 * control every other picker in the builder uses — the shared `Picker` from
 * `@clockwise/ui` — in single-select mode, where the pick replaces rather than
 * adds. One control to learn, it holds a long list, and the search matches
 * descriptions as well as names.
 */
import type { ReportModel } from '@clockwise/semantic-core';
import { Picker } from '@clockwise/ui';

export interface ModelPickerProps {
  models: readonly ReportModel[];
  /** The chosen model's id. */
  value: string;
  onChange: (id: string) => void;
}

export function ModelPicker({ models, value, onChange }: ModelPickerProps) {
  return (
    <div className="rk-modelpick">
      <span className="rk-modelpick-legend">Report on</span>
      <Picker
        title="Report on"
        placeholder="Report on"
        searchPlaceholder="Search subject areas…"
        emptyText="There are no report subject areas."
        options={models.map((m) => ({
          id: m.id,
          label: m.label,
          description: m.description,
        }))}
        value={value}
        // The premise of the report, not a filter: there is no "no subject
        // area" state to clear back to.
        clearable={false}
        onChange={(id) => {
          if (id) onChange(id);
        }}
        // Descriptions run longer than the narrow config rail, so the panel is
        // allowed to outgrow its trigger. It floats over the preview — nothing
        // below it moves.
        popoverWidth="auto"
      />
    </div>
  );
}
